import { makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import React, { lazy, Suspense } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { ContainerDiv, FjCard, FjText, PageTitle } from 'src/components/Common'
import { LibraryGrid } from 'src/components/Library/LibraryGrid'
import { FeedPage, PublicFeedPage } from 'src/components/Page/Page'
import { Paths } from 'src/constants/navigation'
import { sharedAppStateStore } from 'src/store/AppStateStore'
import { getQueryParam } from 'src/utils/urlParams'
import { LibraryCategory } from 'src/pages/Library'
import ContentTabs from 'src/components/Feed/ContentTabs'
import { sharedDataStore } from 'src/store/DataStore'
import { ExternalAcademy } from 'src/models/ExternalAcademy'
import { ViewWrapper } from 'src/components/Common/ViewWrapper'
import { Config } from 'src/models/Config'
import { RedisSurveyBanner } from 'src/components/CustomBranding/RedisSurveyBanner'

const RedisHome = lazy(() => import('src/components/CustomBranding/RedisHome'))

@observer
export class Academy extends React.Component<RouteComponentProps<{ urlPath?: string }>> {
  @observable viewState: 'initialLoad' | 'loading' | 'notFound' | 'idle' = 'initialLoad'
  @observable selectedCategory: LibraryCategory = 'course'
  @observable categories: LibraryCategory[] = ['course', 'learningpath']

  constructor(props) {
    super(props)
    makeObservable(this)
  }

  async componentDidMount() {
    try {
      await Config.getConfigs()
      const { urlPath } = this.props.match.params

      if (!sharedAppStateStore.externalAcademy && !urlPath) {
        this.viewState = 'notFound'
        return
      }

      if (sharedAppStateStore.externalAcademy?.isRedis()) {
        this.viewState = 'idle'
        return
      }

      if (!sharedAppStateStore.externalAcademy && urlPath) {
        this.viewState = 'loading'
        const externalAcademy = await ExternalAcademy.get(undefined, { urlPath })
        sharedAppStateStore.setAcademy(externalAcademy)
      }

      runInAction(() => {
        let contentType = getQueryParam('contentType') as LibraryCategory
        this.selectedCategory = this.categories.includes(contentType) ? contentType : 'course'
        sharedAppStateStore.navigate(Paths.getAcademyPath(this.props.match.params.urlPath, this.selectedCategory))
        this.viewState = 'idle'
      })
    } catch (err) {
      sharedAppStateStore.handleError(err, { alert: false })
      this.viewState = 'notFound'
    }
  }

  handleTabChanged = (category: LibraryCategory) => {
    this.selectedCategory = category
    sharedAppStateStore.navigate(Paths.getAcademyPath(this.props.match.params.urlPath, category))
  }

  render() {
    const PageComponent = sharedDataStore.user.isAnonymous() ? PublicFeedPage : FeedPage
    const pageProps = { title: 'Academy', hideNavbar: true }
    let subNavbar = null

    if (this.viewState === 'notFound') {
      pageProps['hideNavbar'] = false
    }

    if (this.viewState === 'idle') {
      if (sharedAppStateStore.externalAcademy?.isRedis()) pageProps['title'] = undefined
      else pageProps['title'] = `${sharedAppStateStore.externalAcademy.companyName} Academy`

      pageProps['hideNavbar'] = false
      subNavbar = sharedAppStateStore.externalAcademy?.isRedis() ? <RedisSurveyBanner /> : null
    }

    return (
      <PageComponent containerClassName="page-container" subNavbar={subNavbar} {...pageProps}>
        <ViewWrapper viewState={this.viewState}>
          {this.viewState === 'idle' && sharedAppStateStore.externalAcademy?.isRedis() ? (
            <Suspense fallback={<></>}>
              <RedisHome />
            </Suspense>
          ) : this.viewState === 'idle' ? (
            <>
              <ContainerDiv marginBottom="100px">
                <PageTitle marginTop="20px" marginBottom className="academy-headline-text">
                  {sharedAppStateStore.externalAcademy.headline}
                </PageTitle>
                {!!sharedAppStateStore.externalAcademy.subHeadline && (
                  <FjText className="academy-subheadline-text" fontSize="20px">
                    {sharedAppStateStore.externalAcademy.subHeadline}
                  </FjText>
                )}
              </ContainerDiv>
              <FjCard>
                <ContentTabs
                  activeKey={this.selectedCategory}
                  tabChanged={this.handleTabChanged}
                  overrideTabs={this.categories}
                />
                <LibraryGrid
                  showSort
                  showFilter={!!sharedAppStateStore.externalAcademy?.data.publicFilters?.length}
                  apiQueryParams={{ company_id: sharedAppStateStore.externalAcademy?.companyId }}
                  overrideFilters={sharedAppStateStore.externalAcademy?.getOverrideFilters()}
                  {...this.props}
                />
              </FjCard>
            </>
          ) : null}
        </ViewWrapper>
      </PageComponent>
    )
  }
}
